import {
  useGetScopedPathnameFactory,
  useGoToPathFactory,
  useScopedPathnameFactory,
} from '@retailer-platform/dashboard/routing'
import { routes, type DomainRouteName } from '../../routing/routes'

const SHOPPERS_ADMIN_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_SHOPPERS_ADMIN_URL ?? 'https://shoppers-admin.instacart.com'

const LOGISTICS_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_LOGISTICS_URL ?? 'https://logistics.instacart.com'

const CARE_ADMIN_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_CARE_TOOL_ADMIN_URL ?? 'https://care.instacart.tools'

// Payment-processor dashboards where a charge log's gatewayTransactionId resolves to
// the transaction page whose Timeline surfaces the ARN. Hosts (and the Braintree
// merchant account id) are public values that appear in the dashboard URLs themselves,
// not secrets. Stripe's host is public and hardcoded so its links work with no config.
const STRIPE_DASHBOARD_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_STRIPE_DASHBOARD_URL ?? 'https://dashboard.stripe.com'

const BRAINTREE_DASHBOARD_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_BRAINTREE_DASHBOARD_URL ?? 'https://www.braintreegateway.com'

// No hardcoded fallback: the Braintree merchant id is a live account id that isn't
// committed anywhere. Without it we can't build a valid dashboard URL, so a Braintree
// row degrades to id + Copy (no link) until the id is supplied via __RPP_ENV__.
const BRAINTREE_MERCHANT_ID =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_BRAINTREE_MERCHANT_ID ?? null

const ADYEN_DASHBOARD_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_ADYEN_DASHBOARD_URL ?? 'https://ca-live.adyen.com'

export const useDomainScopedPathname = useScopedPathnameFactory<DomainRouteName>()
export const useDomainGetScopedPathname = useGetScopedPathnameFactory<DomainRouteName>()
export const useDomainGoToPath = useGoToPathFactory<DomainRouteName>()

export const buildCustomerUrl = (customerId?: string) =>
  routes['app-admin-customers-and-orders-ipp-customer-details'].replace(
    ':customerId',
    customerId ?? ''
  )

// The order detail page reads `?orderReference=` as an encodeURIComponent'd value that
// use-query-params then URL-encodes again, so it is encoded twice here to match what the page
// itself writes. Passing it saves the page a ListOrdersByCustomer round trip before GetOrder.
export const buildOrderUrl = (
  customerId: string,
  orderId: string,
  orderReference?: string | null
) => {
  const path = routes['app-admin-customers-and-orders-ipp-customer-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)
  return orderReference
    ? `${path}?orderReference=${encodeURIComponent(encodeURIComponent(orderReference))}`
    : path
}

export const buildRestaurantOrderUrl = (customerId: string, orderId: string) =>
  routes['app-admin-customers-and-orders-legacy-care-restaurant-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)

export const buildBatchUrl = (batchId: string) =>
  routes['app-admin-customers-and-orders-legacy-batch-details'].replace(':batchId', batchId)

export const buildCustomerNotesUrl = (customerId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/customers/${customerId}/notes`

// A charge log's instrumentReference is a type-prefixed opaque string
// (`credit_<id>`, `card_<id>`, `ebt_<id>`, ...). Only credit instruments have a
// legacy /credits/<numeric id> page. Mirror the backend's
// payment_instrument_id_from_instrument_reference (require the `credit` prefix and
// take the id segment) and return null for any other instrument type so the caller
// renders plain text instead of a link to a wrong/nonexistent credit page.
export const getCreditId = (instrumentReference: string): string | null => {
  const [prefix, creditId] = instrumentReference.split('_')
  if (prefix !== 'credit' || !creditId) return null
  return creditId
}

export const buildOrderDriversUrl = (orderId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/orders/${orderId}/order_drivers`

export const buildOrderReconciliationsUrl = (orderId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/orders/${orderId}/reconciliations`

export const buildOrderCouponsUrl = (orderId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/orders/${orderId}/coupons`

export const buildShopperUrl = (shopperId: string) =>
  `${SHOPPERS_ADMIN_URL}/care/shoppers/${shopperId}`

export const buildApplicantUrl = (applicantId: string) =>
  `${SHOPPERS_ADMIN_URL}/applicants/${applicantId}`

export const buildShopperBatchUrl = (shopperId: string, batchId: string) =>
  `${SHOPPERS_ADMIN_URL}/care/shoppers/${shopperId}/batches/${batchId}`

// Unscoped fallback for call sites where the batch's actual shopper isn't reliably known
// (e.g. a return batch, which may be handled by a different shopper than the order itself).
export const buildLegacyShopperBatchUrl = (batchId: string) =>
  `${SHOPPERS_ADMIN_URL}/batches/${batchId}`

export const buildDriverBatchUrl = (batchId: string) => `${LOGISTICS_URL}/batches/${batchId}`

export const buildOrderPhotosUrl = (orderId: string) =>
  `${SHOPPERS_ADMIN_URL}/order_delivery_photos/${orderId}`

export const buildFraudMarkersUrl = (userId: string, orderId: string) =>
  `${CARE_ADMIN_URL}/fraud_markers/User/${userId}/Order/${orderId}`

/**
 * Turns an order receipt URL into its PDF variant by appending `.pdf` to the
 * `/receipt` path segment and dropping the fragment.
 *
 * e.g. `https://<domain>/orders/<id>/receipt?token=<t>&full=true#see-all`
 *   -> `https://<domain>/orders/<id>/receipt.pdf?token=<t>&full=true`
 *
 * Operates purely on the string so it works for both absolute and relative
 * receipt URLs (`new URL()` throws on relative inputs). Returns `null` only when
 * there is no receipt URL at all.
 */
export const buildReceiptPdfUrl = (receiptUrl?: string | null): string | null => {
  if (!receiptUrl) return null
  // Drop the fragment (e.g. `#see-all`); the PDF endpoint doesn't use it.
  const withoutHash = receiptUrl.split('#')[0]
  // Split path from query so we only touch the path segment.
  const queryIndex = withoutHash.indexOf('?')
  const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex)
  // Append `.pdf` to the `/receipt` segment; guard against double-appending.
  const pdfPath = path.endsWith('/receipt') ? `${path}.pdf` : path
  return `${pdfPath}${query}`
}

/** Returns full URL (origin + path) for copying or opening in new tab. */
export const buildOrderUrlFull = (
  customerId: string,
  orderId: string,
  orderReference?: string | null
): string => {
  const path = buildOrderUrl(customerId, orderId, orderReference)
  if (typeof window === 'undefined') return path
  const base = path.startsWith('/') ? path : `/${path}`
  return `${window.location.origin}${base}`
}

// Stripe object-id prefixes we recognize as processor transaction ids that resolve on
// the Stripe dashboard's payment Timeline (where the ARN lives). Mirrors legacy
// insta_admin stripe_helper.rb, which links these to the /payments/ path.
const STRIPE_ID_PREFIXES = ['ch_', 'py_', 'pi_', 're_', 'pyr_', 'txn_']

const isStripeId = (id: string): boolean => STRIPE_ID_PREFIXES.some(prefix => id.startsWith(prefix))

export const buildStripeDashboardUrl = (id: string): string =>
  `${STRIPE_DASHBOARD_URL}/payments/${id}`

export const buildBraintreeDashboardUrl = (id: string): string | null =>
  BRAINTREE_MERCHANT_ID
    ? `${BRAINTREE_DASHBOARD_URL}/merchants/${BRAINTREE_MERCHANT_ID}/transactions/${id}`
    : null

export const buildAdyenDashboardUrl = (id: string): string =>
  `${ADYEN_DASHBOARD_URL}/ca/ca/accounts/showTx.shtml?pspReference=${id}&txType=Payment`

/**
 * Deep-links a charge log's gatewayTransactionId into the payment processor's dashboard,
 * where a T3 agent can read the ARN (Stripe payment Timeline, Braintree / Adyen
 * transaction page). Returns null when a valid URL can't be built (unrecognized gateway,
 * or a required base URI / merchant id isn't configured) so the caller degrades to plain
 * id + Copy.
 *
 * Detection prefers the Stripe id prefix (unambiguous, and Stripe is the dominant
 * gateway), then falls back to the paymentGateway enum name, normalized
 * case-insensitively since it arrives as an uppercase proto enum name.
 */
export const buildProcessorDashboardUrl = (
  gatewayTransactionId?: string | null,
  paymentGateway?: string | null
): string | null => {
  if (!gatewayTransactionId) return null
  if (isStripeId(gatewayTransactionId)) return buildStripeDashboardUrl(gatewayTransactionId)
  switch (paymentGateway?.toUpperCase()) {
    case 'STRIPE':
    case 'STRIPE_INTENTS':
      return buildStripeDashboardUrl(gatewayTransactionId)
    case 'BRAINTREE':
      return buildBraintreeDashboardUrl(gatewayTransactionId)
    case 'ADYEN':
      return buildAdyenDashboardUrl(gatewayTransactionId)
    default:
      return null
  }
}
